import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import {
  DollarSign, TrendingUp, Users, Briefcase,
  ArrowUp, ArrowDown, AlertTriangle,
  FileText, Receipt, Star, BarChart3,
  Shield, Target, Repeat
} from 'lucide-react';
import {
  format, parseISO, startOfMonth, endOfMonth, subMonths,
  isWithinInterval, eachMonthOfInterval, differenceInDays
} from 'date-fns';
import { formatCurrency } from '../utils/helpers';
import './OwnerDashboard.css';

const OWNER_EMAILS = [
  'sarah@twosisterscleaningco.com',
  'sarah.keel@twosisterscleaningco.com',
];

const inRange = (dateStr, start, end) => {
  if (!dateStr) return false;
  try {
    return isWithinInterval(parseISO(dateStr), { start, end });
  } catch { return false; }
};

function OwnerDashboard() {
  const { state } = useApp();
  const { user } = useAuth();
  const { clients, employees, jobs, invoices, estimates, complaints, feedbacks } = state;

  const [period, setPeriod] = useState('6');

  const isOwner = user?.email && OWNER_EMAILS.includes(user.email.toLowerCase());

  const now = new Date();
  const monthsBack = parseInt(period);
  const periodStart = startOfMonth(subMonths(now, monthsBack - 1));
  const periodEnd = endOfMonth(now);
  const prevPeriodStart = startOfMonth(subMonths(periodStart, monthsBack));
  const prevPeriodEnd = endOfMonth(subMonths(periodStart, 1));

  const months = eachMonthOfInterval({ start: periodStart, end: periodEnd });

  const thisMonthStart = startOfMonth(now);
  const thisMonthEnd = endOfMonth(now);
  const lastMonthStart = startOfMonth(subMonths(now, 1));
  const lastMonthEnd = endOfMonth(subMonths(now, 1));

  // ===== REVENUE & PROFIT =====
  const financials = useMemo(() => {
    const periodJobs = jobs.filter(j => inRange(j.date, periodStart, periodEnd) && j.status === 'completed');
    const prevJobs = jobs.filter(j => inRange(j.date, prevPeriodStart, prevPeriodEnd) && j.status === 'completed');
    const periodInvoices = invoices.filter(i => inRange(i.createdAt, periodStart, periodEnd));
    const prevInvoices = invoices.filter(i => inRange(i.createdAt, prevPeriodStart, prevPeriodEnd));

    const totalRevenue = periodInvoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);
    const prevRevenue = prevInvoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);

    const totalLaborCost = periodJobs.reduce((sum, job) => {
      const hours = job.actual_duration_hours || (job.actual_duration_min ? job.actual_duration_min / 60 : job.duration ? job.duration / 60 : 0);
      const workers = [...new Set([...(job.team || []), ...(job.helpers || [])])];
      let cost = 0;
      workers.forEach(empId => {
        const emp = employees.find(e => e.id === empId);
        cost += hours * (emp?.hourlyRate || emp?.payRate || 15);
      });
      if (workers.length === 0) cost = hours * 15 * 2;
      return sum + cost;
    }, 0);

    const grossProfit = totalRevenue - totalLaborCost;
    const profitMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;
    const revenueGrowth = prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue) * 100 : 0;

    const outstanding = invoices
      .filter(i => i.status === 'pending' || i.status === 'overdue')
      .reduce((s, i) => s + (i.total || 0), 0);

    const overdueInvoices = invoices.filter(i => i.status === 'overdue');
    const overdueTotal = overdueInvoices.reduce((s, i) => s + (i.total || 0), 0);

    const monthlyData = months.map(monthStart => {
      const monthEnd = endOfMonth(monthStart);
      const mInv = invoices.filter(i => inRange(i.createdAt, monthStart, monthEnd) && i.status === 'paid');
      const mJobs = jobs.filter(j => inRange(j.date, monthStart, monthEnd) && j.status === 'completed');
      const rev = mInv.reduce((s, i) => s + (i.total || 0), 0);
      const labor = mJobs.reduce((sum, job) => {
        const hours = job.actual_duration_hours || (job.actual_duration_min ? job.actual_duration_min / 60 : job.duration ? job.duration / 60 : 0);
        const workers = [...new Set([...(job.team || []), ...(job.helpers || [])])];
        let cost = 0;
        workers.forEach(empId => {
          const emp = employees.find(e => e.id === empId);
          cost += hours * (emp?.hourlyRate || emp?.payRate || 15);
        });
        if (workers.length === 0) cost = hours * 15 * 2;
        return sum + cost;
      }, 0);
      return {
        label: format(monthStart, 'MMM yyyy'),
        shortLabel: format(monthStart, 'MMM'),
        revenue: rev,
        labor,
        profit: rev - labor,
        jobs: mJobs.length,
      };
    });

    return {
      totalRevenue, prevRevenue, revenueGrowth, totalLaborCost,
      grossProfit, profitMargin, outstanding, overdueTotal,
      overdueCount: overdueInvoices.length, monthlyData,
      jobCount: periodJobs.length, prevJobCount: prevJobs.length,
    };
  }, [jobs, invoices, employees, months, periodStart, periodEnd, prevPeriodStart, prevPeriodEnd]);

  // ===== CLIENT ANALYTICS =====
  const clientStats = useMemo(() => {
    const active = clients.filter(c => c.status === 'active');
    const inactive = clients.filter(c => c.status === 'inactive');
    const recurring = active.filter(c => ['weekly', 'biweekly', 'monthly'].includes(c.frequency));

    const recurringRevenue = recurring.reduce((sum, c) => {
      const clientJobs = jobs.filter(j => j.clientId === c.id && j.status === 'completed');
      const last3Months = clientJobs.filter(j => inRange(j.date, startOfMonth(subMonths(now, 2)), periodEnd));
      if (last3Months.length === 0) return sum;
      const avgPerJob = last3Months.reduce((s, j) => s + (j.price || 0), 0) / last3Months.length;
      const freq = c.frequency === 'weekly' ? 4.33 : c.frequency === 'biweekly' ? 2.17 : 1;
      return sum + (avgPerJob * freq);
    }, 0);

    const newThisMonth = clients.filter(c => inRange(c.createdAt, thisMonthStart, thisMonthEnd));
    const newLastMonth = clients.filter(c => inRange(c.createdAt, lastMonthStart, lastMonthEnd));

    const topClients = active
      .map(c => {
        const clientInv = invoices.filter(i => i.clientId === c.id && i.status === 'paid' && inRange(i.createdAt, periodStart, periodEnd));
        const totalSpent = clientInv.reduce((s, i) => s + (i.total || 0), 0);
        return { ...c, totalSpent, invoiceCount: clientInv.length };
      })
      .filter(c => c.totalSpent > 0)
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, 10);

    return {
      totalActive: active.length,
      totalInactive: inactive.length,
      recurringCount: recurring.length,
      monthlyRecurringRevenue: recurringRevenue,
      newThisMonth: newThisMonth.length,
      newLastMonth: newLastMonth.length,
      topClients,
    };
  }, [clients, jobs, invoices, now, thisMonthStart, thisMonthEnd, lastMonthStart, lastMonthEnd, periodStart, periodEnd]);

  // ===== ESTIMATE PIPELINE =====
  const pipelineStats = useMemo(() => {
    const periodEstimates = estimates.filter(e => inRange(e.createdAt, periodStart, periodEnd));
    const total = periodEstimates.length;
    const approved = periodEstimates.filter(e => e.status === 'approved' || e.status === 'converted').length;
    const declined = periodEstimates.filter(e => e.status === 'declined').length;
    const pending = periodEstimates.filter(e => e.status === 'pending_review' || e.status === 'sent').length;

    const conversionRate = total > 0 ? (approved / total) * 100 : 0;
    const pendingValue = estimates
      .filter(e => e.status === 'pending_review' || e.status === 'sent')
      .reduce((s, e) => s + (e.total || e.price || 0), 0);

    return { total, approved, declined, pending, conversionRate, pendingValue };
  }, [estimates, periodStart, periodEnd]);

  // ===== EMPLOYEE PERFORMANCE =====
  const employeeStats = useMemo(() => {
    const activeEmps = employees.filter(e => e.status === 'active');
    const periodJobs = jobs.filter(j => inRange(j.date, periodStart, periodEnd) && j.status === 'completed');

    const empPerformance = activeEmps.map(emp => {
      const empJobs = periodJobs.filter(j =>
        (j.team || []).includes(emp.id) || (j.helpers || []).includes(emp.id)
      );
      const totalHours = empJobs.reduce((sum, j) => {
        const hours = j.actual_duration_hours || (j.actual_duration_min ? j.actual_duration_min / 60 : j.duration ? j.duration / 60 : 0);
        const workers = [...new Set([...(j.team || []), ...(j.helpers || [])])];
        return sum + (workers.length > 0 ? hours / workers.length : hours);
      }, 0);
      const revenue = empJobs.reduce((sum, j) => {
        const workers = [...new Set([...(j.team || []), ...(j.helpers || [])])];
        return sum + ((j.price || 0) / Math.max(workers.length, 1));
      }, 0);
      const laborCost = totalHours * (emp.hourlyRate || emp.payRate || 15);

      return {
        id: emp.id,
        name: `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
        role: emp.role,
        jobs: empJobs.length,
        hours: Math.round(totalHours * 10) / 10,
        revenue: Math.round(revenue),
        laborCost: Math.round(laborCost),
        efficiency: revenue > 0 ? Math.round((1 - laborCost / revenue) * 100) : 0,
      };
    }).filter(e => e.jobs > 0).sort((a, b) => b.revenue - a.revenue);

    const totalLabor = empPerformance.reduce((s, e) => s + e.laborCost, 0);
    const avgEfficiency = empPerformance.length > 0
      ? empPerformance.reduce((s, e) => s + e.efficiency, 0) / empPerformance.length
      : 0;

    return { activeCount: activeEmps.length, empPerformance, totalLabor, avgEfficiency };
  }, [employees, jobs, periodStart, periodEnd]);

  // ===== COMPLAINTS & FEEDBACK =====
  const satisfactionStats = useMemo(() => {
    const openComplaints = complaints.filter(c => c.status === 'open').length;
    const totalComplaints = complaints.filter(c => inRange(c.createdAt, periodStart, periodEnd)).length;
    const resolvedComplaints = complaints.filter(c => c.status === 'resolved' && inRange(c.createdAt, periodStart, periodEnd)).length;
    const recentFeedback = feedbacks.filter(f => inRange(f.createdAt, periodStart, periodEnd));
    const avgRating = recentFeedback.length > 0
      ? recentFeedback.reduce((s, f) => s + (f.rating || 0), 0) / recentFeedback.length
      : 0;

    return { openComplaints, totalComplaints, resolvedComplaints, avgRating, feedbackCount: recentFeedback.length };
  }, [complaints, feedbacks, periodStart, periodEnd]);

  // ===== CASH FLOW / AGING =====
  const cashFlow = useMemo(() => {
    const unpaid = invoices.filter(i => i.status === 'pending' || i.status === 'overdue');
    const aging = { current: 0, days30: 0, days60: 0, days90plus: 0 };
    unpaid.forEach(inv => {
      const daysOld = inv.createdAt ? differenceInDays(now, parseISO(inv.createdAt)) : 0;
      const amount = inv.total || 0;
      if (daysOld <= 30) aging.current += amount;
      else if (daysOld <= 60) aging.days30 += amount;
      else if (daysOld <= 90) aging.days60 += amount;
      else aging.days90plus += amount;
    });

    const paidThisMonth = invoices
      .filter(i => i.status === 'paid' && inRange(i.paidAt || i.createdAt, thisMonthStart, thisMonthEnd))
      .reduce((s, i) => s + (i.total || 0), 0);
    const issuedThisMonth = invoices
      .filter(i => inRange(i.createdAt, thisMonthStart, thisMonthEnd))
      .reduce((s, i) => s + (i.total || 0), 0);
    const collectionRate = issuedThisMonth > 0 ? (paidThisMonth / issuedThisMonth) * 100 : 0;

    return { aging, paidThisMonth, issuedThisMonth, collectionRate, unpaidCount: unpaid.length };
  }, [invoices, now, thisMonthStart, thisMonthEnd]);

  // Chart helpers
  const maxRevenue = Math.max(...financials.monthlyData.map(m => m.revenue), 1);

  const greeting = (() => {
    const h = now.getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  if (!isOwner) {
    return (
      <div className="owner-dash">
        <div className="owner-restricted">
          <Shield size={48} />
          <h2>Owner Access Required</h2>
          <p>This dashboard is restricted to business owners.</p>
          <Link to="/admin/dashboard" className="owner-back-link">Go to Admin Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="owner-dash">
      {/* Header */}
      <div className="owner-header">
        <div className="owner-header-left">
          <h1>{greeting}, Sarah</h1>
          <p className="owner-subtitle">Business overview for the last {period} months</p>
        </div>
        <div className="owner-header-right">
          <select value={period} onChange={e => setPeriod(e.target.value)} className="owner-period-select">
            <option value="3">Last 3 Months</option>
            <option value="6">Last 6 Months</option>
            <option value="12">Last 12 Months</option>
          </select>
          <Link to="/admin/dashboard" className="owner-admin-link">Admin Dashboard</Link>
          <Link to="/admin/reports" className="owner-admin-link">Detailed Reports</Link>
        </div>
      </div>

      {/* Top KPI Cards */}
      <div className="owner-kpi-grid">
        <div className="owner-kpi revenue">
          <div className="owner-kpi-icon"><DollarSign size={22} /></div>
          <div className="owner-kpi-body">
            <span className="owner-kpi-value">{formatCurrency(financials.totalRevenue)}</span>
            <span className="owner-kpi-label">Total Revenue</span>
            <span className={`owner-kpi-change ${financials.revenueGrowth >= 0 ? 'up' : 'down'}`}>
              {financials.revenueGrowth >= 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
              {Math.abs(financials.revenueGrowth).toFixed(1)}% vs prior period
            </span>
          </div>
        </div>
        <div className="owner-kpi profit">
          <div className="owner-kpi-icon"><TrendingUp size={22} /></div>
          <div className="owner-kpi-body">
            <span className="owner-kpi-value">{formatCurrency(financials.grossProfit)}</span>
            <span className="owner-kpi-label">Gross Profit</span>
            <span className="owner-kpi-sub">{financials.profitMargin.toFixed(1)}% margin</span>
          </div>
        </div>
        <div className="owner-kpi recurring">
          <div className="owner-kpi-icon"><Repeat size={22} /></div>
          <div className="owner-kpi-body">
            <span className="owner-kpi-value">{formatCurrency(clientStats.monthlyRecurringRevenue)}</span>
            <span className="owner-kpi-label">Est. Monthly Recurring</span>
            <span className="owner-kpi-sub">{clientStats.recurringCount} recurring clients</span>
          </div>
        </div>
        <div className={`owner-kpi ${financials.overdueTotal > 0 ? 'warning' : ''}`}>
          <div className="owner-kpi-icon"><AlertTriangle size={22} /></div>
          <div className="owner-kpi-body">
            <span className="owner-kpi-value">{formatCurrency(financials.outstanding)}</span>
            <span className="owner-kpi-label">Outstanding Balance</span>
            <span className="owner-kpi-sub">{financials.overdueCount} overdue</span>
          </div>
        </div>
      </div>

      {/* Revenue Trend Chart */}
      <div className="owner-section">
        <div className="owner-section-header">
          <h2><BarChart3 size={20} /> Revenue & Profit Trend</h2>
        </div>
        <div className="owner-chart">
          <div className="owner-chart-legend">
            <span className="owner-legend-item"><span className="owner-legend-dot revenue" /> Revenue</span>
            <span className="owner-legend-item"><span className="owner-legend-dot labor" /> Labor</span>
            <span className="owner-legend-item"><span className="owner-legend-dot profit" /> Profit</span>
          </div>
          <div className="owner-bar-chart">
            {financials.monthlyData.map((m, i) => (
              <div key={i} className="owner-bar-group">
                <div className="owner-bar-values">
                  <span className="owner-bar-val revenue">{formatCurrency(m.revenue).replace('.00', '')}</span>
                </div>
                <div className="owner-bar-stack">
                  <div className="owner-bar revenue" style={{ height: `${(m.revenue / maxRevenue) * 180}px` }}>
                    <div className="owner-bar labor" style={{ height: `${m.revenue > 0 ? (m.labor / m.revenue) * 100 : 0}%` }} />
                  </div>
                </div>
                <span className="owner-bar-label">{m.shortLabel}</span>
                <span className="owner-bar-jobs">{m.jobs} jobs</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Two-column: Client Analytics + Estimate Pipeline */}
      <div className="owner-two-col">
        {/* Client Analytics */}
        <div className="owner-section">
          <div className="owner-section-header">
            <h2><Users size={20} /> Client Analytics</h2>
          </div>
          <div className="owner-stats-grid">
            <div className="owner-stat">
              <span className="owner-stat-value">{clientStats.totalActive}</span>
              <span className="owner-stat-label">Active Clients</span>
            </div>
            <div className="owner-stat">
              <span className="owner-stat-value">{clientStats.totalInactive}</span>
              <span className="owner-stat-label">Inactive</span>
            </div>
            <div className="owner-stat">
              <span className="owner-stat-value">{clientStats.recurringCount}</span>
              <span className="owner-stat-label">Recurring</span>
            </div>
            <div className="owner-stat">
              <span className="owner-stat-value highlight">{clientStats.newThisMonth}</span>
              <span className="owner-stat-label">New This Month</span>
            </div>
          </div>

          {clientStats.topClients.length > 0 && (
            <div className="owner-table-wrap">
              <h3>Top Clients by Revenue</h3>
              <table className="owner-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Invoices</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {clientStats.topClients.map(c => (
                    <tr key={c.id}>
                      <td>
                        <Link to={`/admin/clients/${c.id}`} className="owner-client-link">
                          {c.firstName} {c.lastName}
                        </Link>
                      </td>
                      <td>{c.invoiceCount}</td>
                      <td className="owner-money">{formatCurrency(c.totalSpent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Estimate Pipeline + Satisfaction */}
        <div className="owner-section-stack">
          <div className="owner-section">
            <div className="owner-section-header">
              <h2><Target size={20} /> Estimate Pipeline</h2>
            </div>
            <div className="owner-pipeline">
              <div className="owner-pipeline-stat primary">
                <span className="owner-pipeline-val">{pipelineStats.conversionRate.toFixed(0)}%</span>
                <span className="owner-pipeline-label">Conversion Rate</span>
              </div>
              <div className="owner-pipeline-stat">
                <span className="owner-pipeline-val">{pipelineStats.total}</span>
                <span className="owner-pipeline-label">Total Estimates</span>
              </div>
              <div className="owner-pipeline-stat success">
                <span className="owner-pipeline-val">{pipelineStats.approved}</span>
                <span className="owner-pipeline-label">Approved</span>
              </div>
              <div className="owner-pipeline-stat danger">
                <span className="owner-pipeline-val">{pipelineStats.declined}</span>
                <span className="owner-pipeline-label">Declined</span>
              </div>
            </div>
            <div className="owner-pipeline-pending">
              <FileText size={16} />
              <span>{pipelineStats.pending} pending estimates worth {formatCurrency(pipelineStats.pendingValue)}</span>
            </div>
          </div>

          <div className="owner-section">
            <div className="owner-section-header">
              <h2><Star size={20} /> Customer Satisfaction</h2>
            </div>
            <div className="owner-satisfaction">
              <div className="owner-satisfaction-row">
                <span className="owner-satisfaction-label">Open Complaints</span>
                <span className={`owner-satisfaction-value ${satisfactionStats.openComplaints > 0 ? 'warn' : 'good'}`}>
                  {satisfactionStats.openComplaints}
                </span>
              </div>
              <div className="owner-satisfaction-row">
                <span className="owner-satisfaction-label">Resolved (period)</span>
                <span className="owner-satisfaction-value">{satisfactionStats.resolvedComplaints}</span>
              </div>
              <div className="owner-satisfaction-row">
                <span className="owner-satisfaction-label">Feedback Received</span>
                <span className="owner-satisfaction-value">{satisfactionStats.feedbackCount}</span>
              </div>
              {satisfactionStats.avgRating > 0 && (
                <div className="owner-satisfaction-row">
                  <span className="owner-satisfaction-label">Avg Rating</span>
                  <span className="owner-satisfaction-value good">
                    <Star size={14} /> {satisfactionStats.avgRating.toFixed(1)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cash Flow / Aging */}
      <div className="owner-section">
        <div className="owner-section-header">
          <h2><Receipt size={20} /> Cash Flow & Invoice Aging</h2>
        </div>
        <div className="owner-cashflow-grid">
          <div className="owner-cashflow-card">
            <span className="owner-cashflow-label">Collected This Month</span>
            <span className="owner-cashflow-value good">{formatCurrency(cashFlow.paidThisMonth)}</span>
          </div>
          <div className="owner-cashflow-card">
            <span className="owner-cashflow-label">Issued This Month</span>
            <span className="owner-cashflow-value">{formatCurrency(cashFlow.issuedThisMonth)}</span>
          </div>
          <div className="owner-cashflow-card">
            <span className="owner-cashflow-label">Collection Rate</span>
            <span className={`owner-cashflow-value ${cashFlow.collectionRate >= 80 ? 'good' : 'warn'}`}>
              {cashFlow.collectionRate.toFixed(0)}%
            </span>
          </div>
          <div className="owner-cashflow-card">
            <span className="owner-cashflow-label">Unpaid Invoices</span>
            <span className="owner-cashflow-value">{cashFlow.unpaidCount}</span>
          </div>
        </div>

        <div className="owner-aging">
          <h3>Aging Breakdown</h3>
          <div className="owner-aging-bars">
            {[
              { label: 'Current (0-30d)', amount: cashFlow.aging.current, cls: 'green' },
              { label: '31-60 days', amount: cashFlow.aging.days30, cls: 'yellow' },
              { label: '61-90 days', amount: cashFlow.aging.days60, cls: 'orange' },
              { label: '90+ days', amount: cashFlow.aging.days90plus, cls: 'red' },
            ].map(({ label, amount, cls }) => {
              const total = cashFlow.aging.current + cashFlow.aging.days30 + cashFlow.aging.days60 + cashFlow.aging.days90plus;
              const pct = total > 0 ? (amount / total) * 100 : 0;
              return (
                <div key={label} className="owner-aging-row">
                  <span className="owner-aging-label">{label}</span>
                  <div className="owner-aging-bar-wrap">
                    <div className={`owner-aging-bar ${cls}`} style={{ width: `${Math.max(pct, 2)}%` }} />
                  </div>
                  <span className="owner-aging-amount">{formatCurrency(amount)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Employee Performance */}
      <div className="owner-section">
        <div className="owner-section-header">
          <h2><Briefcase size={20} /> Team Performance</h2>
          <div className="owner-section-meta">
            <span>{employeeStats.activeCount} active employees</span>
            <span className="owner-section-meta-sep">|</span>
            <span>Avg efficiency: {employeeStats.avgEfficiency.toFixed(0)}%</span>
          </div>
        </div>

        {employeeStats.empPerformance.length > 0 && (
          <div className="owner-table-wrap">
            <table className="owner-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Role</th>
                  <th>Jobs</th>
                  <th>Hours</th>
                  <th>Revenue</th>
                  <th>Labor Cost</th>
                  <th>Efficiency</th>
                </tr>
              </thead>
              <tbody>
                {employeeStats.empPerformance.map(emp => (
                  <tr key={emp.id}>
                    <td>
                      <Link to={`/admin/employees/${emp.id}`} className="owner-client-link">
                        {emp.name}
                      </Link>
                    </td>
                    <td><span className="owner-role-badge">{emp.role}</span></td>
                    <td>{emp.jobs}</td>
                    <td>{emp.hours}h</td>
                    <td className="owner-money">{formatCurrency(emp.revenue)}</td>
                    <td className="owner-money">{formatCurrency(emp.laborCost)}</td>
                    <td>
                      <span className={`owner-efficiency ${emp.efficiency >= 60 ? 'high' : emp.efficiency >= 40 ? 'mid' : 'low'}`}>
                        {emp.efficiency}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default OwnerDashboard;
